import { initDataUser, retrieveRawInitData } from '@telegram-apps/sdk-react';
import { loadCurrentUser, type CurrentUser } from '@/entities/user';
import { setAuthorizationProvider } from '@/shared/api';
import { env } from '@/shared/config';
import { isTelegram } from '@/shared/lib/platform';
import { initMetrika, reportClientId } from '@/shared/lib/metrika';
import { initTelegram, mountTelegramUi } from '@/shared/lib/telegram';
import { dropSessionOnUnauthorized, prepareSession, sessionAuthorization } from '@/shared/lib/webSession';
import { DEFAULT_LOCALE, initI18n, type SupportedLocale } from '@/shared/lib/i18n';

/**
 * Догадка о языке интерфейса до ответа бэкенда — по языку клиента Telegram.
 * Уточняется на `user.locale` из `GET /me`, как только он подгрузится (см.
 * `useCurrentUser`) — это финальный, авторитетный источник.
 */
function guessLocale(): SupportedLocale {
  const languageCode = (isTelegram() ? initDataUser()?.language_code : navigator.language.slice(0, 2)) ?? '';
  if (languageCode === 'ru') return 'ru';
  // На запуск MVP интерфейс только ru/en — de/pt/fr/es не угадываем, хотя
  // переводы лежат (см. `APP_LOCALES` онбординга и `LOCALES` на бэкенде).
  return DEFAULT_LOCALE;
}

/**
 * initData для заголовка авторизации.
 *
 * В dev, если настоящего нет, — тестовый из `VITE_MOCK_LAUNCH_PARAMS`. Подмена
 * окружения Telegram (`mock-env`) в браузере телефона срабатывает не везде: на
 * iPhone по ссылке тоннеля все запросы, включая `/me`, уходили вовсе без
 * заголовка и получали 401 (2026-09-17), хотя на Android и Windows та же ссылка
 * работала. Бэкенд тестовые данные принимает — они подписаны токеном бота.
 * В проде ветка вырезается вместе с `env.isDev`.
 */
function readInitData(): string | undefined {
  let initData: string | undefined;
  try {
    initData = retrieveRawInitData();
  } catch {
    initData = undefined;
  }
  initData = freshest(initData);
  return initData ?? (env.isDev ? env.mockLaunchParams : undefined);
}

/**
 * Самый свежий initData за жизнь страницы — по `auth_date`.
 *
 * SDK на каждый вызов заново ищет данные запуска: в текущем адресе, потом в
 * адресе первой загрузки страницы (`performance`), потом в `sessionStorage`.
 * Роутер сразу убирает hash из адреса, и дальше на телефоне какой-то из
 * запасных источников отдавал initData суточной давности: `/me` на старте
 * проходил со свежим, а все запросы следом получали 401 `Init data expired`
 * (прод, 2026-10-11; с компьютера работало, перезапуск Telegram не помогал).
 * Какой именно источник — на устройстве не выяснено. Поэтому запоминаем
 * лучший из виденных и старым его не заменяем.
 */
let bestInitData: string | undefined;

function authDate(initData: string): number {
  return Number(new URLSearchParams(initData).get('auth_date')) || 0;
}

function freshest(candidate: string | undefined): string | undefined {
  if (candidate && (!bestInitData || authDate(candidate) > authDate(bestInitData))) {
    bestInitData = candidate;
  }
  return bestInitData;
}

/**
 * Всё, что должно произойти до первого рендера. Порядок: initData нужен уже
 * первому запросу к бэкенду и языковой догадке — он есть сразу после
 * `initTelegram()`. Дальше всё параллельно (P1-2 в `PERFORMANCE_PLAN.md`):
 *
 * - `GET /me` уходит сейчас, а не после первого рендера из `RootRedirect`:
 *   ответ ложится в общий кеш `user:me`, и `RootRedirect` с провайдером языка
 *   его просто подхватывают — один круг до сервера прячется за bootstrap;
 * - вьюпорт и цвета Telegram (`mountTelegramUi`) — их CSS-переменные нужны
 *   первому экрану;
 * - переводы.
 *
 * Возвращает сам запрос `/me`: по его ошибке `main.tsx` узнаёт, что человека
 * нет в белом списке, и рисует экран «в разработке».
 */
export async function bootstrap(): Promise<{ currentUser: Promise<CurrentUser> | null }> {
  if (!isTelegram()) return bootstrapWeb();

  await initTelegram();

  // retrieveRawInitData валиден только после init()/restoreInitData().
  setAuthorizationProvider(() => {
    const initData = readInitData();
    return initData ? `tma ${initData}` : undefined;
  });

  // Ошибку здесь не показываем: неудачный запрос в кеш не ложится, и
  // `RootRedirect` повторит его сам и покажет экран ошибки с «Повторить».
  const currentUser = loadCurrentUser();
  currentUser.catch(() => undefined);

  await Promise.all([mountTelegramUi(), initI18n(guessLocale())]);
  return { currentUser };
}

/**
 * Обычная вкладка браузера (веб-версия, см. WEB.md): Telegram SDK не
 * поднимаем, авторизуемся токеном сессии.
 *
 * Сессию ждём до первого рендера: гостю из рекламы она заводится здесь же,
 * и без ожидания `RootRedirect` успел бы спросить `/me` без токена. Нет
 * сессии и пришли не из рекламы — `currentUser: null`, `main.tsx` рисует
 * экран входа вместо приложения.
 */
async function bootstrapWeb(): Promise<{ currentUser: Promise<CurrentUser> | null }> {
  setAuthorizationProvider(sessionAuthorization);
  // Счётчик — первым: колбэк входа через Яндекс в `prepareSession` уже шлёт цель.
  initMetrika();
  const [hasSession] = await Promise.all([prepareSession(), initI18n(guessLocale())]);
  if (!hasSession) return { currentUser: null };
  reportClientId();
  return { currentUser: loadWebUser() };
}

/**
 * `/me` веб-версии: 401 по сессии — токен забывается (см.
 * `dropSessionOnUnauthorized`). Отдельно — для старта с экрана входа без
 * перезагрузки (`main.tsx`): гость заведён, приложение рисуется сразу.
 */
export function loadWebUser(): Promise<CurrentUser> {
  const currentUser = loadCurrentUser().catch(dropSessionOnUnauthorized);
  currentUser.catch(() => undefined);
  return currentUser;
}
